import { Injectable } from '@angular/core';
import { catchError, mergeMap, take, tap } from 'rxjs/operators';
import { onlyTruthy } from 'src/app/utils/rxjs-custom-operators/rxjs-custom-operators.utils';
import { Observable, BehaviorSubject, EMPTY, timer } from 'rxjs';
import { ApiService } from '../api/api.service';
import { LoadingErrorsService } from '../loading-errors/loading-errors.service';
import { UserPermissions } from './user-permissions/user-permissions';
import { ActivatedRoute, Router } from '@angular/router';
import { CcAdminApiHelperService } from './cc-admin-api-helper.service';
import { HttpErrorResponse } from '@angular/common/http';

@Injectable({
	providedIn: 'root',
})
export class CcAdminApiService {
	public openAgreementsModal: () => void;

	/** Subject to track if user is authorized and their graph space has been initialized */
	private graphInitializedSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
	public get graphInitialized$(): Observable<boolean> {
		return this.graphInitializedSubject.asObservable().pipe(onlyTruthy());
	}

	private _user: UserPermissions;
	public get user() {
		return this._user;
	}

	private readonly publicRoutes = ['/self-registration', '/eula'];

	constructor(
		private apiService: ApiService,
		private loadingErrorsService: LoadingErrorsService,
		private router: Router,
		private ccAdminApiHelperService: CcAdminApiHelperService,
		private activatedRoute: ActivatedRoute
	) {
		// Once token is ready, initialize graph space
		this.ccAdminApiHelperService.tokenReady
			.pipe(
				onlyTruthy(),
				take(1),
				mergeMap(() => this.ccAdminApiHelperService.isAuthorized().pipe(tap(u => (this._user = u)))),
				mergeMap(() =>
					this.apiService.getInitialize().pipe(
						catchError(err => {
							this.loadingErrorsService.openErrorPage(
								err?.status?.toString(),
								err?.statusText,
								$localize`Failed to retrieve customer initialization status. If issues persist, contact your system administrator.`
							);
							return EMPTY;
						})
					)
				)
			)
			.subscribe(() => {
				this.graphInitializedSubject.next(true);
				// Check for presence of "agency" query param
				this.activatedRoute.queryParams.pipe(take(1)).subscribe(params => {
					const agencyId = params?.agency;
					if (!agencyId) {
						return;
					}
					// If desired and actual agencyId are defined and different, attempt to switch to desired agencyId
					if (agencyId !== this.user.agencyId) {
						this.switchAgency(agencyId);
					}
				});
			});

		// If token is not ready after 10 seconds, go to error page
		timer(10000).subscribe(() => {
			if (
				!this.publicRoutes.some(route => this.router.url.includes(route)) &&
				!this.ccAdminApiHelperService.getToken()
			) {
				this.loadingErrorsService.openErrorPage(
					'401 / 408',
					$localize`Authentication error`,
					$localize`Timed out during authentication with Command Central. If issues persist, contact your system administrator.`
				);
			}
		});
	}

	// Switch agency to the one specified
	switchAgency(agencyId: string) {
		this.apiService.switchAgency(agencyId, this.ccAdminApiHelperService.getToken()).subscribe({
			next: () => {
				// Clear agency query param and reload page
				this.router
					.navigate([], { queryParams: { agency: null }, queryParamsHandling: 'merge' })
					.then(() => location.reload());
			},
			error: (e: HttpErrorResponse) => {
				if (e.status === 400) {
					// Clear agency query param
					this.router.navigate([], { queryParams: { agency: null }, queryParamsHandling: 'merge' });
				}
			},
		});
	}
}

// Copyright (c) Motorola Solutions Inc. All Rights Reserved.
